import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Card,
  Dropdown,
  DropdownButton,
  OverlayTrigger,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import TopLimitDropdown from "../../../../../common/TopLimitDropdown";
import AccordionComponent from "../../../../../common/Accordion";
import { useDispatch } from "react-redux";
import { getTranscriberToDO } from "../../../../../services/TranscriberDashboardAPI";

interface TranscriberToDoProps {
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
}

const TranscriberToDo: React.FC<TranscriberToDoProps> = ({
  openDropdown,
  setOpenDropdown,
}) => {
  const [limit, setLimit] = useState<number>(5);
  const [limitLabel, setLimitLabel] = useState<string>("Top 5");
  const [caseData, setCaseData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchLatestCases = async (limitValue: number) => {
    setLoading(true);

    try {
      const data = await getTranscriberToDO(limitValue);
      setCaseData(data);
    } catch (error) {
      console.error("Failed to fetch latest cases:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLatestCases(limit);
  }, [limit]);

  const handleLimitChange = (value: number, label: string) => {
    setLimit(value);
    setLimitLabel(label);
  };

  const [LatestCaseKey, setLatestCaseKey] = useState<string | null>("0");

  const handleBodyAccordionSelect = (key: string | null) => {
    // Prevent collapsing if the same key is clicked
    if (LatestCaseKey === key) {
      return; // do nothing
    }
    setLatestCaseKey(key || "0");
  };

  const LatestCaseAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={LatestCaseKey}
      onSelect={handleBodyAccordionSelect}
    />
  );

  const TruncatedWithTooltip = ({ text }: { text: string }) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const el = textRef.current;
      if (el) {
        setIsTruncated(el.scrollWidth > el.clientWidth);
      }
    }, [text]);

    const content = (
      <span
        ref={textRef}
        className="fw-semibold text-truncate d-inline-block"
        style={{ maxWidth: "100%" }}
      >
        {text}
      </span>
    );

    return isTruncated ? (
      <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
        {content}
      </OverlayTrigger>
    ) : (
      content
    );
  };

  return (
    <>
      {LatestCaseAccordion([
        {
          title: (
            <div className="d-flex align-items-center w-100">
              {/* Left side title block */}
              <div className="me-auto d-flex flex-column">
                <span className="fw-semibold">My To-Do Items</span>
              </div>

              {/* Dropdown */}
              <div onClick={(e) => e.stopPropagation()}>
                <TopLimitDropdown
                  title={limitLabel}
                  onSelect={handleLimitChange}
                  show={openDropdown === "transcriber_toDo"}
                  onToggle={(isOpen) =>
                    setOpenDropdown(isOpen ? "transcriber_toDo" : null)
                  }
                />
              </div>

              {/* Icon */}
            </div>
          ),
          content: (
            <>
              <Card className="border-0 ">
                <Card.Body className="d-flex flex-column p-0 bg-theme">
                  <div
                    style={{
                      maxHeight: limit > 5 ? "250px" : "auto",
                      overflowY: "auto",
                    }}
                    className="d-flex flex-column gap-1 flex-grow-1"
                  >
                    {caseData.length === 0
                      ? ""
                      : caseData.map((item, index) => (
                          <div
                            key={index}
                            className="latest-case py-1"
                            style={{
                              borderBottom: "1px solid #e9ecef",
                              fontSize: "13px",
                              lineHeight: "1.2",
                            }}
                          >
                            <div className="d-flex justify-content-between">
                              {/* Left Column */}
                              <div
                                className="d-flex flex-column text-truncate"
                                style={{ maxWidth: "65%" }}
                              >
                                <TruncatedWithTooltip text={item.patientName} />

                                <span className="text-muted text-truncate mt-1">
                                  <a
                                    href="#/mytodos"
                                    className="text-blue-600 underline dashbardCaseAnchr"
                                  >
                                    {item.examTypeDesc || "-"}
                                  </a>
                                </span>
                              </div>

                              {/* Right Column */}
                            </div>
                          </div>
                        ))}
                  </div>
                </Card.Body>
              </Card>
            </>
          ),
        },
      ])}
    </>
  );
};

export default TranscriberToDo;
